import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { readComplaintPhotos } from '@/lib/complaintPhotos';

function useAvailablePhoto(value) {
  const [ready, setReady] = useState(false);
  const serialized = JSON.stringify(value || null);
  useEffect(() => {
    let active = true;
    setReady(false);
    const photos = readComplaintPhotos(JSON.parse(serialized)).urls;
    const images = photos.map(url => {
      const image = new Image();
      image.onload = () => { if (active) setReady(true); };
      image.src = /^(data:|blob:|https?:)/i.test(url) ? url : `${process.env.REACT_APP_BACKEND_URL || ''}/${url.replace(/^\//, '')}`;
      return image;
    });
    return () => { active = false; images.forEach(image => { image.onload = null; }); };
  }, [serialized]);
  return ready;
}

export default function ComplaintCompletion({ complaint, busy, onComplete }) {
  const [note, setNote] = useState('');
  const [workedAt, setWorkedAt] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const hasOriginal = useAvailablePhoto([complaint.photo_urls, complaint.photo_url]);
  const hasProof = useAvailablePhoto([complaint.resolution_photos, complaint.proof_photos]);
  const filesValid = files.length <= 5 && files.every(file => ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) && file.size > 0 && file.size <= 10 * 1024 * 1024);
  const missing = [];
  if (!complaint.property_name || !complaint.category || !complaint.description?.trim()) missing.push('Data gedung, kategori, dan deskripsi komplain');
  if (!complaint.assigned_to_email) missing.push('Petugas — pilih melalui Job Desk');
  if (!workedAt || !Number.isFinite(Date.parse(workedAt))) missing.push('Tanggal dan jam pengerjaan');
  if (!note.trim()) missing.push('Catatan hasil pengerjaan');
  if (!hasOriginal) missing.push('Foto awal komplain yang dapat ditampilkan');
  if (!hasProof && !files.length) missing.push('Minimal satu foto bukti pengerjaan');
  if (!filesValid) missing.push('Maksimal 5 foto JPG, PNG, WEBP, atau GIF, masing-masing maksimal 10 MB');
  if (Number(complaint.owner_cost) > 0 && !complaint.owner_cost_description?.trim()) missing.push('Keterangan biaya');
  const submit = async () => {
    if (missing.length || busy || submitting) return;
    setSubmitting(true); setError('');
    try {
      const data = new FormData();
      data.append('note', note.trim());
      data.append('worked_at', new Date(workedAt).toISOString());
      files.forEach(file => data.append('files', file));
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/complaints/${encodeURIComponent(complaint.complaint_id)}/complete`, data,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      onComplete(res.data);
    } catch (e) { setError(e.response?.data?.detail || 'Komplain gagal diselesaikan. Data belum ditandai selesai; silakan coba lagi.'); }
    finally { setSubmitting(false); }
  };
  if (complaint.status === 'closed') return null;
  return <section className="mt-4 p-4 rounded-xl border border-isk-teal/20 bg-isk-teal/5 space-y-3" aria-label="Penyelesaian komplain">
    <h4 className="font-semibold text-isk-text">Hasil Pengerjaan</h4>
    <label className="block text-sm">Tanggal dan jam pengerjaan *
      <Input type="datetime-local" value={workedAt} onChange={event => setWorkedAt(event.target.value)} disabled={submitting} className="mt-1" />
    </label>
    <label className="block text-sm">Catatan hasil pengerjaan *
      <Textarea value={note} onChange={event => setNote(event.target.value)} disabled={submitting} placeholder="Jelaskan tindakan dan hasil perbaikan" className="mt-1" />
    </label>
    <label className="block text-sm">Foto bukti pengerjaan *
      <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" disabled={submitting} onChange={event => setFiles(Array.from(event.target.files || []))} className="block mt-2 w-full text-xs" />
    </label>
    <p className="text-xs text-isk-muted">Minimal satu foto. Maksimal 5 foto baru, masing-masing 10 MB.{hasProof ? ' Foto bukti yang sudah tersimpan dapat digunakan.' : ''}</p>
    {files.length > 0 && <p className="text-xs break-words">{files.map(file => file.name).join(', ')}</p>}
    {missing.length > 0 && <div className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3"><p className="font-medium">Belum lengkap:</p><ul className="list-disc pl-5 mt-1">{missing.map(item => <li key={item}>{item}</li>)}</ul></div>}
    {busy && <p className="text-xs text-isk-muted">Menunggu data tersimpan...</p>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <Button onClick={submit} disabled={missing.length > 0 || busy || submitting} className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-full">{submitting ? 'Menyelesaikan...' : 'Selesai'}</Button>
  </section>;
}
